import { useCallback, useEffect, useRef } from 'react';
import {
  ensureMemberAutocompleteFooterStyles,
  findMemberAutocompleteDropdown,
  setupMemberAutocompleteDropdownStyles,
} from './member-autocomplete-dropdown-footer.util';

const FOOTER_APPLY_DELAY_MS = 50;

/**
 * Injects Attorney-style dropdown footer (helper text + result count)
 * for a MemberAutocomplete instance identified by rootClassName.
 *
 * Uses delayed DOM lookup + style injection because the Autocomplete paper is
 * portaled and has no footer slot in MUI v4 lab. See the module comment in
 * `member-autocomplete-dropdown-footer.util.js` for why this is intentional
 * and what a React `PaperComponent` migration would look like.
 *
 * @param {object} params
 * @param {string} params.rootClassName
 * @param {Array} params.memberOptions
 * @param {string} [params.footerLeftText]
 */
export const useMemberAutocompleteDropdownFooter = ({
  rootClassName,
  memberOptions = [],
  footerLeftText,
}) => {
  const memberOptionsRef = useRef(memberOptions);
  const footerLeftTextRef = useRef(footerLeftText);
  const applyTimerRef = useRef(null);

  useEffect(() => {
    memberOptionsRef.current = memberOptions;
  }, [memberOptions]);

  useEffect(() => {
    footerLeftTextRef.current = footerLeftText;
  }, [footerLeftText]);

  useEffect(() => {
    ensureMemberAutocompleteFooterStyles();
  }, []);

  const applyFooterNow = useCallback(() => {
    const { paper, listbox } = findMemberAutocompleteDropdown(rootClassName);
    if (!paper) {
      return;
    }

    setupMemberAutocompleteDropdownStyles(
      paper,
      listbox,
      memberOptionsRef.current,
      footerLeftTextRef.current,
    );
  }, [rootClassName]);

  const scheduleFooterApply = useCallback(() => {
    if (applyTimerRef.current) {
      window.clearTimeout(applyTimerRef.current);
    }
    applyTimerRef.current = window.setTimeout(() => {
      applyFooterNow();
      applyTimerRef.current = null;
    }, FOOTER_APPLY_DELAY_MS);
  }, [applyFooterNow]);

  useEffect(() => {
    const rootSelector = `.${String(rootClassName || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .join('.')}`;

    if (rootSelector === '.') {
      return undefined;
    }

    let inputElement = null;
    let autocompleteRoot = null;

    const handleFocus = () => {
      scheduleFooterApply();
    };

    const handleOpenTrigger = () => {
      scheduleFooterApply();
    };

    const setupTimer = window.setTimeout(() => {
      autocompleteRoot = document.querySelector(rootSelector);
      if (!autocompleteRoot) {
        return;
      }

      inputElement = autocompleteRoot.querySelector(
        '.MuiAutocomplete-inputRoot input',
      );
      if (inputElement) {
        inputElement.addEventListener('focus', handleFocus);
      }
      autocompleteRoot.addEventListener('click', handleOpenTrigger);
    }, FOOTER_APPLY_DELAY_MS);

    return () => {
      window.clearTimeout(setupTimer);
      if (applyTimerRef.current) {
        window.clearTimeout(applyTimerRef.current);
      }
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
      }
      if (autocompleteRoot) {
        autocompleteRoot.removeEventListener('click', handleOpenTrigger);
      }
    };
  }, [rootClassName, scheduleFooterApply]);

  // Refresh footer when results or helper text change while the popup is open.
  useEffect(() => {
    scheduleFooterApply();
  }, [memberOptions, footerLeftText, scheduleFooterApply]);

  return { applyFooter: scheduleFooterApply };
};
